// Maps a quiz answer payload to constructor scene state.
// Quiz lives on imperial-ritual.kz; sends payloads via URL params or postMessage.

import useStore from '../store/useStore';
import { isPamyatniki } from './variant';

// Match by case-insensitive fragment to be robust against punctuation drift
// (e.g. "Гранит «Курты»" vs `Гранит "Курты"` in the catalog).
const COLOR_PATTERN = {
  red:   /красн/i,
  kurty: /курт/i,
  black: /ч[её]рн/i,
  green: /зел[её]н/i,
  grey:  /сер[ыо]|grey|gray/i,
};

// API stones carry name + price but rarely a hex color, so the constructor
// keeps a hardcoded fallback (mirrors MONUMENT_COLOR_VARIANTS in Sidebar).
const STONE_FALLBACK = {
  red:   { color: '#8b0000', texture_url: '/textures/красный-гранит.jpg' },
  kurty: { color: '#6b5b4f', texture_url: '/textures/курты.webp' },
  black: { color: '#1a1a1a', texture_url: '/textures/черный_гранит.jpeg' },
  green: { color: '#2d4a24', texture_url: '/textures/зеленый_гранит.jpg' },
  grey:  { color: '#7a7a7a', texture_url: '/textures/grey_granit.jpg' },
};

const FOUNDATION_ELEMENT_NAME = {
  earth:    'earth',
  concrete: 'concrete',
};

// Match flooring by Russian-name fragment so admin renames don't break the quiz.
// 'granite' is handled separately because it carries a stone color choice.
const FLOORING_NAME_PATTERN = {
  tile:   /тротуарн|плитка\s*сер/i,
  grass:  /газон|трава/i,
  marble: /мрамор/i,
};

// Hollow-flowerbed filling: maps quiz key → flooring element fragment.
// Picks an element from the flooring category flagged `is_flowerbed_filling`
// (same source the constructor's filling picker uses).
const FILLING_NAME_PATTERN = {
  grass:  /газон|трава/i,
  marble: /мрамор/i,
};

const MONUMENT_ELEMENT_NAME = {
  vertical: 'standard_small',
  family:   'family_stele',
};
// Pamyatniki: арочная «Мусульманская» форма (квиз лендинга шлёт monument=muslim).
// Для blago ветка сворачивается Терсером — карта и бандл байт-идентичны.
if (isPamyatniki) MONUMENT_ELEMENT_NAME.muslim = 'muslim_arch';

// Fence variants: 3 catalog elements that all live in the 'fence' category.
// Quiz sends fenceVariant=simple|standard|standard2 with extras containing 'fence'.
const FENCE_VARIANT_NAME = {
  simple:    'metal_simple',
  standard:  'ogradka',
  standard2: 'ogradka_2',
};

// Placed objects (bench/table): admin-created elements often don't have the
// canonical `name`/`model_type` from the seed, so we also match by Russian
// category name fragment and fall back to the first element in that category.
const EXTRA_LOOKUP = {
  border: { categoryName: 'border', elementName: 'concrete_border', isLayer: true },
  fence:  { categoryName: 'fence',  elementName: 'metal_simple',    isLayer: true },
  bench:  { categoryName: 'bench',  categoryNamePattern: /скам/i,    elementName: 'wooden',     modelType: 'bench', isLayer: false },
  table:  { categoryName: 'table',  categoryNamePattern: /стол/i,    elementName: 'medium',     modelType: 'table', isLayer: false },
  // Vase lives in the "Аксессуары" category and uses model_type='cylinder' (shared with other accessories),
  // so we match by element name fragment as a more reliable signal.
  vase:   { categoryName: 'accessories', categoryNamePattern: /аксесс/i, elementName: 'vase_small', elementNamePattern: /ваз/i, isLayer: false },
  // Engravings auto-attach to the monument's front face in Scene3D — no manual position needed.
  engraving_portrait: { categoryName: 'engraving', categoryNamePattern: /гравиров/i, elementName: 'engraving_portrait', modelType: 'engraving_portrait', isLayer: false },
  engraving_fio:      { categoryName: 'engraving', categoryNamePattern: /гравиров/i, elementName: 'engraving_text',     modelType: 'engraving_text',     isLayer: false },
};
// Pamyatniki: гравировка полумесяца (квиз шлёт extras=engraving_moon для мусульманской
// формы). Добавляется общей веткой как обычная гравировка-картинка. Для blago запись в
// карту сворачивается Терсером — объект и бандл байт-идентичны.
if (isPamyatniki) EXTRA_LOOKUP.engraving_moon = { categoryName: 'engraving', categoryNamePattern: /гравиров/i, elementName: 'engraving_moon', modelType: 'engraving_portrait', isLayer: false };
// Pamyatniki: эпитафия — одна строка свободного текста внизу грани (квиз шлёт extras=epitaph + текст в quiz.epitaph).
if (isPamyatniki) EXTRA_LOOKUP.epitaph = { categoryName: 'engraving', categoryNamePattern: /гравиров/i, elementName: 'engraving_epitaph', modelType: 'engraving_epitaph', isLayer: false };

function findElementByName(elements, name) {
  return elements.find(e => e.name === name);
}
function findCategoryByName(categories, name) {
  return categories.find(c => c.name === name);
}
function findStoneByColorKey(stones, colorKey) {
  const re = COLOR_PATTERN[colorKey];
  if (!re) return null;
  return stones.find(s => re.test(s.name_ru || ''));
}
function findFlowerbedElement(elements, shape) {
  // Pick "Средний" size as a sensible default; user can swap in constructor.
  return elements.find(e => e.model_type === 'flowerbed' && e.flowerbed_shape === shape && e.name_ru === 'Средний')
      || elements.find(e => e.model_type === 'flowerbed' && e.flowerbed_shape === shape);
}

// Synthesize a granite-облицовка border (mirrors Sidebar's pickStoneBorder).
// Returns null for 'concrete' or unknown materials so caller can fall back to the catalog element.
function buildStoneBorder(catalogBorderEl, elements, stones, materialKey) {
  if (!materialKey || materialKey === 'concrete') return null;
  const stone = findStoneByColorKey(stones, materialKey);
  const fallback = STONE_FALLBACK[materialKey];
  if (!stone && !fallback) return null;
  const anyBorderEl = catalogBorderEl
    || elements.find(e => e.model_type === 'border')
    || {};
  const thicknessM = 0.02;
  const borderHeight = Number(anyBorderEl.height) || 0.1;
  const borderSectionWidth = Number(anyBorderEl.section_width) || 0.5;
  return {
    ...anyBorderEl,
    id: `stone-border-${stone?.id || materialKey}`,
    name: 'stone_border',
    name_ru: `Бордюр — ${stone?.name_ru || materialKey}`,
    model_type: 'border',
    price: Number(stone?.price_per_cubic_meter) || 0,
    price_unit: 'cubic_meter',
    stone_id: stone?.id,
    height: borderHeight,
    width: thicknessM,
    section_width: borderSectionWidth,
    pricing_thickness_m: thicknessM,
    // Don't override `color` — Scene3D multiplies it with the texture and would
    // tint the granite (e.g. red fallback × red texture = saturated dark red).
    // Let anyBorderEl's neutral concrete gray pass through (mirrors Sidebar's
    // pickStoneBorder which also doesn't set color).
    texture_url: fallback?.texture_url || stone?.texture_url || stone?.thumbnail_url || anyBorderEl.texture_url,
    thumbnail_url: stone?.thumbnail_url || anyBorderEl.thumbnail_url,
    colorName: stone?.name_ru,
  };
}

function buildStoneEnriched(element, stone, fallback) {
  // Memorial-style elements (monument, pedestal, flowerbed) priced per m³ via stone.
  // Texture priority: local fallback first — Scene3D's matcher recognizes the
  // canonical /textures/<russian>.jpg paths and may not detect a Supabase URL
  // returned by the admin-uploaded stone record.
  return {
    ...element,
    element_id: element.id,
    color: fallback?.color || stone?.color || element.color || '#666666',
    texture_url: fallback?.texture_url || stone?.texture_url || stone?.thumbnail_url,
    colorName: stone?.name_ru,
    price: stone?.price_per_cubic_meter || 0,
    price_unit: 'cubic_meter',
  };
}

export function applyQuizState(quiz, catalog) {
  const { categories = [], elements = [], stones = [] } = catalog || {};
  if (!elements.length || !categories.length) return; // catalog not loaded yet

  const store = useStore.getState();
  const {
    setPerimeterSize,
    setSelectedFoundation,
    setSelectedFloor,
    setSelectedFence,
    setSelectedBorder,
    addPlacedElement,
  } = store;

  // Reset prior selections so updates are idempotent
  useStore.setState({
    placedElements: [],
    selectedFloor: null,
    selectedFoundation: null,
    selectedFence: null,
    selectedBorder: null,
    customItems: [],
    flowerbedFilling: null,
    quizViewMode: quiz.viewMode || null,
  });

  // 1. Perimeter size
  if (quiz.w && quiz.l) {
    const w = Math.max(1, Math.min(10, +quiz.w));
    const l = Math.max(1, Math.min(10, +quiz.l));
    setPerimeterSize(w, l);
  }

  // 2. Foundation
  const foundationName = FOUNDATION_ELEMENT_NAME[quiz.foundation];
  if (foundationName) {
    const el = findElementByName(elements, foundationName);
    if (el) setSelectedFoundation(el);
  }

  // 3. Flooring — granite uses its own element + stone color, others by name fragment
  if (quiz.flooring === 'granite') {
    const graniteEl = elements.find(e => e.name === 'granite_tiles')
                  || elements.find(e => e.model_type === 'floor' && /гранитн/i.test(e.name_ru || ''));
    const floorStone = quiz.flooringColor ? findStoneByColorKey(stones, quiz.flooringColor) : null;
    const floorFallback = quiz.flooringColor ? STONE_FALLBACK[quiz.flooringColor] : null;
    if (graniteEl) {
      const pricePerM3 = floorStone?.price_per_cubic_meter || 0;
      const thicknessM = 0.02; // matches Sidebar's granite-floor pricing
      setSelectedFloor({
        ...graniteEl,
        name: graniteEl.name || 'granite_tiles',
        name_ru: floorStone?.name_ru || graniteEl.name_ru,
        price: pricePerM3 * thicknessM,
        price_unit: 'sqmeter',
        color: floorFallback?.color || floorStone?.color || graniteEl.color,
        texture_url: floorFallback?.texture_url || floorStone?.texture_url,
        colorName: floorStone?.name_ru,
      });
    }
  } else {
    const flooringPattern = FLOORING_NAME_PATTERN[quiz.flooring];
    if (flooringPattern) {
      const el = elements.find(e => e.model_type === 'floor' && flooringPattern.test(e.name_ru || ''));
      if (el) setSelectedFloor(el);
    }
  }

  // Layout anchors. Proportional offsets so the arrangement scales from
  // small (2×3) to large (6×8) plots without anything piling up at center:
  //   memorial → back-center · flowerbed → just in front · bench/table → front corners.
  const w = useStore.getState().perimeterWidth;
  const l = useStore.getState().perimeterLength;
  const flowerbedZ = -l * 0.05;
  const layout = {
    // Glued to the flowerbed: 55 cm between centers — seed depths suggested
    // ~42.5 cm but real rendered models are larger, so this is the empirical
    // value where the pedestal sits flush behind the flowerbed without overlap.
    memorialZ:  flowerbedZ - 0.55,
    flowerbedZ,
    // Bench center this far from the front fence. Pulled forward so the
    // empty visiting strip behind the front fence shrinks.
    frontZ:      Math.max(l * 0.20, l / 2 - 0.30),
    // Bench center this far from the side fence. 0.45 m keeps the bench
    // safely inside the plot for any rendered bench width.
    sideX:       Math.max(w * 0.20, w / 2 - 0.45),
  };

  // 4. Monument + pedestal (always paired, same stone color).
  // Add pedestal FIRST and reposition BEFORE the monument is added — Scene3D's
  // getPedestalBeneath stacks the monument on the pedestal at first render
  // using its current position.
  const stone = quiz.color ? findStoneByColorKey(stones, quiz.color) : null;
  const stoneFallback = quiz.color ? STONE_FALLBACK[quiz.color] : null;
  const monumentName = MONUMENT_ELEMENT_NAME[quiz.monument];
  if (monumentName) {
    // Семейная стела идёт со своей тумбой (pedestal_family, 80×20×20) — правило
    // «стела + 10 см» к ней не применяется. Для остальных форм — обычная тумба.
    const pedestalEl = (quiz.monument === 'family' && findElementByName(elements, 'pedestal_family'))
                    || findElementByName(elements, 'pedestal_medium')
                    || elements.find(e => e.model_type === 'pedestal');
    if (pedestalEl) {
      // Памятники: квиз может передать свой размер тумбы (см). Если передан —
      // переопределяем w/h/d перед stone-enrichment, чтобы цена пересчиталась.
      const pedestalElSized = (quiz.pedestalW || quiz.pedestalD || quiz.pedestalH)
        ? {
            ...pedestalEl,
            width:  quiz.pedestalW ? Number(quiz.pedestalW) / 100 : pedestalEl.width,
            depth:  quiz.pedestalD ? Number(quiz.pedestalD) / 100 : pedestalEl.depth,
            height: quiz.pedestalH ? Number(quiz.pedestalH) / 100 : pedestalEl.height,
          }
        : pedestalEl;
      addPlacedElement(buildStoneEnriched(pedestalElSized, stone, stoneFallback));
      const placed = useStore.getState().placedElements;
      const lastPed = placed[placed.length - 1];
      if (lastPed) {
        useStore.getState().updatePlacedElement(lastPed.id, {
          position: { x: 0, y: 0, z: layout.memorialZ },
        });
      }
    }
    const monumentElBase = findElementByName(elements, monumentName)
                    || (quiz.monument === 'vertical'
                        ? elements.find(e => /вертикальн/i.test(e.name_ru || ''))
                        : elements.find(e => /семейн/i.test(e.name_ru || '')));
    // Памятники: если квиз передал пользовательские размеры (см для W/D, м для H),
    // переопределяем геометрию стелы — buildStoneEnriched уже выставит price_unit
    // = cubic_meter, и Scene3D/CostCalculator посчитают цену от этих размеров.
    const monumentEl = monumentElBase && (quiz.monumentW || quiz.monumentD || quiz.monumentH)
      ? {
          ...monumentElBase,
          width:  quiz.monumentW ? Number(quiz.monumentW) / 100 : monumentElBase.width,
          depth:  quiz.monumentD ? Number(quiz.monumentD) / 100 : monumentElBase.depth,
          height: quiz.monumentH ? Number(quiz.monumentH)       : monumentElBase.height,
        }
      : monumentElBase;
    if (monumentEl) {
      addPlacedElement(buildStoneEnriched(monumentEl, stone, stoneFallback));
      // Match the pedestal's x/z so getPedestalBeneath finds it and stacks
      // the monument on top (Scene3D sets the y from pedestal height).
      const placed = useStore.getState().placedElements;
      const lastMon = placed[placed.length - 1];
      if (lastMon) {
        useStore.getState().updatePlacedElement(lastMon.id, {
          position: { x: 0, y: 0, z: layout.memorialZ },
        });
      }
    }
  }

  // 5. Flowerbed (shape: 'p' | 'rectangle' | 'none').
  // Place in front of the pedestal (toward the camera) so it doesn't sit
  // inside the monument/pedestal stack at the origin.
  if (quiz.flowerbed && quiz.flowerbed !== 'none') {
    const fbEl = findFlowerbedElement(elements, quiz.flowerbed);
    if (fbEl) {
      addPlacedElement(buildStoneEnriched(fbEl, stone, stoneFallback));
      const placed = useStore.getState().placedElements;
      const lastFb = placed[placed.length - 1];
      if (lastFb) {
        useStore.getState().updatePlacedElement(lastFb.id, {
          position: { x: 0, y: 0, z: layout.flowerbedZ },
        });
      }
    }

    // Hollow flowerbed only — pick a flooring element flagged as filling.
    // Earth (no filling) is the default and stored as null.
    if (quiz.flowerbed === 'p' && quiz.flowerbedFilling) {
      const pat = FILLING_NAME_PATTERN[quiz.flowerbedFilling];
      if (pat) {
        const fillEl = elements.find(e => e.is_flowerbed_filling === true && pat.test(e.name_ru || ''))
                    || elements.find(e => e.model_type === 'floor' && pat.test(e.name_ru || ''));
        if (fillEl) {
          useStore.getState().setFlowerbedFilling(fillEl);
        } else {
          console.warn(`[quiz] No filling element for "${quiz.flowerbedFilling}" — set is_flowerbed_filling=true on the catalog flooring item, or rename it to match ${pat}`);
        }
      }
    }
  }

  // 6. Extras (comma-separated string or array)
  const extras = Array.isArray(quiz.extras)
    ? quiz.extras
    : (typeof quiz.extras === 'string' && quiz.extras.length ? quiz.extras.split(',') : []);

  // Default placement for extras. Bench + table grouped on the left as
  // a sitting set: bench at front, table 40 cm behind it (close enough
  // that a visitor sitting on the bench can use the table). Right side
  // stays open for the visiting path. Vase sits next to the memorial.
  const extraPositions = {
    bench: { x: -layout.sideX, y: 0, z: layout.frontZ },
    table: { x: -layout.sideX, y: 0, z: layout.frontZ - 0.40 },
    vase:  { x:  w * 0.25, y: 0, z: layout.memorialZ + 0.3 },
  };

  for (const key of extras) {
    const map = EXTRA_LOOKUP[key];
    if (!map) continue;

    // Resolve category: try exact `name`, then Russian-name fragment (admin
    // catalogs often have only `name_ru` set, e.g. "Скамейка" / "Стол").
    const cat = findCategoryByName(categories, map.categoryName)
             || (map.categoryNamePattern && categories.find(c => map.categoryNamePattern.test(c.name_ru || '')));

    // Resolve element: preferred name → matching model_type → first element in
    // the resolved category. The last fallback covers admin-created entries
    // that have neither the canonical `name` nor `model_type` set.
    let el = cat ? elements.find(e => e.category_id === cat.id && e.name === map.elementName) : null;
    if (!el && map.modelType) {
      el = (cat && elements.find(e => e.category_id === cat.id && e.model_type === map.modelType))
        || elements.find(e => e.model_type === map.modelType);
    }
    // For categories where multiple elements share a generic model_type (e.g. accessories
    // → cylinder), match the desired element by Russian-name fragment.
    if (!el && cat && map.elementNamePattern) {
      el = elements.find(e => e.category_id === cat.id && map.elementNamePattern.test(e.name_ru || ''));
    }
    if (!el && cat) {
      el = elements.find(e => e.category_id === cat.id);
    }
    if (!el) {
      console.warn(`[quiz] No catalog element found for "${key}" (category=${map.categoryName}, name=${map.elementName}, model_type=${map.modelType || 'n/a'})`);
      continue;
    }
    if (key === 'border') {
      const stoneBorder = buildStoneBorder(el, elements, stones, quiz.borderMaterial);
      setSelectedBorder(stoneBorder || el);
    }
    else if (key === 'fence') {
      // If the visitor picked a fence variant, prefer that catalog element.
      const variantName = FENCE_VARIANT_NAME[quiz.fenceVariant];
      const variantEl = variantName ? findElementByName(elements, variantName) : null;
      setSelectedFence(variantEl || el);
    }
    else if (key === 'engraving_portrait' || key === 'engraving_fio') {
      // Гравировка ФИО: текст пробрасываем из квиза (Scene3D рисует на canvas).
      // Для семейной стелы — две независимые надписи (ФИО 1 / ФИО 2).
      const fio1 = key === 'engraving_fio' ? {
        lastName:   quiz.fioLastName   || '',
        firstName:  quiz.fioFirstName  || '',
        patronymic: quiz.fioPatronymic || '',
      } : {};
      const fio2 = key === 'engraving_fio' ? {
        lastName:   quiz.fio2LastName   || '',
        firstName:  quiz.fio2FirstName  || '',
        patronymic: quiz.fio2Patronymic || '',
      } : {};
      // Family stele has two faces — place an engraving on each. Price doubles
      // automatically since each is its own placedElement.
      if (quiz.monument === 'family') {
        addPlacedElement({ ...el, element_id: el.id, engravingOffsetX: -0.30, ...fio1 });
        addPlacedElement({ ...el, element_id: el.id, engravingOffsetX:  0.30, ...fio2 });
      } else {
        addPlacedElement({ ...el, element_id: el.id, ...fio1 });
      }
    }
    else if (key === 'epitaph') {
      // Эпитафия: свободный текст из квиза, одна курсивная строка внизу грани.
      addPlacedElement({ ...el, element_id: el.id, epitaphText: quiz.epitaph || '' });
    }
    else {
      addPlacedElement({ ...el, element_id: el.id });
      const pos = extraPositions[key];
      if (pos) {
        // addPlacedElement assigns a fresh id and resets position to origin —
        // grab the just-added element and move it to the desired spot.
        const placed = useStore.getState().placedElements;
        const last = placed[placed.length - 1];
        if (last) useStore.getState().updatePlacedElement(last.id, { position: pos });
      }
    }
  }
}

// Read quiz state from URL query params (initial load case).
export function readQuizFromURL() {
  if (typeof window === 'undefined') return null;
  const p = new URLSearchParams(window.location.search);
  if (!p.has('w') && !p.has('foundation') && !p.has('monument')) return null;
  return {
    w:             p.get('w'),
    l:             p.get('l'),
    foundation:    p.get('foundation'),
    flooring:      p.get('flooring'),
    flooringColor: p.get('flooringColor'),
    monument:      p.get('monument'),
    color:         p.get('color'),
    // Памятники: пользовательские размеры стелы. W/D в см, H в м (как в квизе).
    monumentW:     p.get('monumentW'),
    monumentD:     p.get('monumentD'),
    monumentH:     p.get('monumentH'),
    // Размеры тумбы (всё в см) — отдельно от памятника.
    pedestalW:     p.get('pedestalW'),
    pedestalD:     p.get('pedestalD'),
    pedestalH:     p.get('pedestalH'),
    flowerbed:     p.get('flowerbed'),
    flowerbedFilling: p.get('flowerbedFilling'),
    extras:        p.get('extras') || '',
    borderMaterial: p.get('borderMaterial'),
    fenceVariant:  p.get('fenceVariant'),
    // Текст ФИО для гравировки (пробрасывается квизом памятников).
    // Семейная стела использует две независимые надписи fio* и fio2*.
    fioLastName:    p.get('fioLastName')    || '',
    fioFirstName:   p.get('fioFirstName')   || '',
    fioPatronymic:  p.get('fioPatronymic')  || '',
    fio2LastName:   p.get('fio2LastName')   || '',
    fio2FirstName:  p.get('fio2FirstName')  || '',
    fio2Patronymic: p.get('fio2Patronymic') || '',
    // Текст эпитафии (одна строка, пробрасывается квизом памятников).
    epitaph:        p.get('epitaph')        || '',
  };
}

export function isEmbedMode() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('embed') === '1';
}
